import { Badge, cn } from '@design-system';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { CarePhaseCostSummary } from '@/components/CarePhaseCostSummary';
import { CareSettingsSlideOutContent, SlideOutSheet } from '@/components';
import {
  OnboardingLayout,
  useSlideSetup,
  useCareSettingsSlide,
  CustomizePlanButtonGroup,
} from '@/features/client-onboarding';
import {
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFamilyCaregivingPhaseThree,
} from '@/pages/OnboardingFamilyCaregiving';
import { OnboardingCarePhasesSummary } from '@/pages/OnboardingCarePhasesSummary';

import { CalendarIcon, ClockIcon } from 'lucide-react';

import { BsBuildingAdd } from 'react-icons/bs';
import { PiBuildingLight, PiHospitalLight, PiWarehouseLight } from 'react-icons/pi';

import { isInferenceEdited } from '@/util/isInferenceEdited';
import { carePhaseDefs, CareEnvironment, CarePhase } from '@shared';

import type { FunctionComponent } from 'react';

interface CarePhaseProps {
  carePhase: CarePhase;
  slidePage: FunctionComponent<ClientIdProps>;
}

function OnboardingCareSettings({ carePhase, slidePage }: CarePhaseProps) {
  const {
    client,
    clientFirstName,
    clientCurrentCarePhase,
    selectedCarePhaseCosts,
    phaseAge,
    carePhaseDurationMonths,
    applicableCareSettings,
    carePhaseStartAge,
    isDurationCustom,
    selectedCareSetting,
    isCareSettingHome,
    handleButtonClick,
    careSettingCosts,
    nextButtonLabel,
  } = useCareSettingsSlide(carePhase);

  useSlideSetup({
    slidePage,
    nextButtonLabel,
    nextSlidePage: determineNextSlide(isCareSettingHome, carePhase),
    disableNextButton: selectedCareSetting === null,
    errorModalMessage: 'Oops! You need to select a care setting to continue.',
  });

  const isCustomInference = isInferenceEdited(client, 'ltcAtAge');

  return (
    <OnboardingLayout>
      <div className="flex flex-col justify-center gap-6 py-6 md:py-16">
        <div className="flex w-full flex-wrap items-start justify-between gap-x-6 gap-y-4">
          <div className="space-y-2">
            <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-4xl">
              {carePhaseDefs[carePhase].label}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={cn('text-sm font-normal', {
                  'pr-0.5!': isCustomInference,
                })}
              >
                <CalendarIcon className="size-4!" />
                <div>
                  {carePhase === clientCurrentCarePhase ? (
                    <>
                      Began at age{' '}
                      <span className="text-primary font-medium">
                        {Math.round(carePhaseStartAge!)}
                      </span>
                    </>
                  ) : (
                    <>
                      Estimated begin:{' '}
                      <span className="text-primary font-medium">
                        {Math.round(phaseAge)} years old
                      </span>
                    </>
                  )}
                  {isCustomInference && (
                    <Badge
                      variant="secondary"
                      className="ml-2"
                    >
                      edited
                    </Badge>
                  )}
                </div>
              </Badge>
              <Badge
                variant="outline"
                className={cn('text-sm font-normal', {
                  'pr-0.5!': isDurationCustom,
                })}
              >
                <ClockIcon className="size-4!" />
                <div>
                  <>
                    Estimated duration:{' '}
                    <span className="text-primary font-medium">
                      {carePhaseDurationMonths}{' '}
                      {carePhase === clientCurrentCarePhase ? 'more ' : ''} months
                    </span>
                  </>
                  {isDurationCustom && (
                    <Badge
                      variant="secondary"
                      className="ml-2"
                    >
                      Edited
                    </Badge>
                  )}
                </div>
              </Badge>
            </div>
          </div>
          <CustomizePlanButtonGroup client={client} />
        </div>
        <div className="border-primary/10 bg-primary/5 flex flex-col gap-4 gap-x-8 rounded-lg border p-4 md:mt-8 md:grid-cols-2  md:flex-row md:p-6 lg:mb-0">
          <div className="flex flex-col md:w-[55%] md:max-w-[70%]">
            <h2 className="text-foreground mb-4 text-base font-normal leading-6 md:mb-0 md:text-xl">
              Select
              {carePhase === clientCurrentCarePhase
                ? ` where ${clientFirstName} is living during ${carePhaseDefs[carePhase].label}.`
                : ` where ${clientFirstName} will live during ${carePhaseDefs[carePhase].label}.`}
            </h2>

            <div className="flex flex-col md:mt-8">
              {/* care setting buttons */}
              <div className="flex flex-wrap justify-between gap-4 md:gap-x-8">
                {applicableCareSettings.map(careSettingData => (
                  <button
                    key={careSettingData.dbId}
                    className={cn(
                      `min-w-53.75 relative col-span-1 flex h-36 flex-1 flex-col items-center justify-center rounded-md border px-1 py-2 pb-3 text-center hover:border-2 md:px-4 md:py-0 [@media_(min-width:_1047px)]:h-48`,
                      `${
                        selectedCareSetting === careSettingData.dbId
                          ? 'border-green-600 bg-green-50'
                          : '  border-primary-300 bg-background hover:bg-gray-50'
                      } `,
                    )}
                    data-testid={(() => {
                      switch (careSettingData.dbId) {
                        case CareEnvironment.home:
                          return 'care-settings-option-home';
                        case CareEnvironment.independentLiving:
                          return 'care-settings-option-independent';
                        case CareEnvironment.assistedLiving:
                          return 'care-settings-option-assisted';
                        case CareEnvironment.fullCareFacility:
                          return 'care-settings-option-fullcare';
                        default:
                          return undefined as unknown as string;
                      }
                    })()}
                    onClick={() => handleButtonClick(careSettingData.dbId)}
                  >
                    {/* radio button circle */}
                    <div
                      className={`absolute left-1 top-1 h-5 w-5 rounded-full border md:left-4 md:top-4 ${
                        selectedCareSetting === careSettingData.dbId
                          ? 'border-green-600'
                          : 'border-border'
                      }`}
                    >
                      {selectedCareSetting === careSettingData.dbId && (
                        <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-600" />
                      )}
                    </div>

                    {/* predicted badge */}
                    {careSettingData.dbId === selectedCarePhaseCosts.recommendedCareEnvironment && (
                      <Badge className="absolute right-1 top-1 md:right-3 md:top-3">
                        Predicted Preference
                      </Badge>
                    )}

                    {/* icon and label */}
                    <div className="mt-8 flex flex-col items-center">
                      <div className="flex items-center">
                        {(() => {
                          switch (careSettingData.dbId) {
                            case CareEnvironment.home:
                              return <PiWarehouseLight className="text-2xl md:text-4xl" />;
                            case CareEnvironment.independentLiving:
                              return <PiBuildingLight className="text-2xl md:text-4xl" />;
                            case CareEnvironment.assistedLiving:
                              return <BsBuildingAdd className="text-2xl md:h-8 md:w-8" />;
                            case CareEnvironment.fullCareFacility:
                              return <PiHospitalLight className="text-2xl md:text-4xl" />;
                          }
                        })()}
                      </div>
                      <div className="text-foreground mt-2 text-sm font-semibold md:text-lg">
                        {careSettingData.label}
                      </div>
                    </div>

                    {/* cost */}
                    <div className="flex justify-end md:mt-1">
                      <span className="text-foreground inline-flex items-center text-sm font-normal leading-8 md:text-base">
                        {(() => {
                          const costData = careSettingCosts.find(
                            c => c.dbId === careSettingData.dbId,
                          );
                          return costData?.isHome ? (
                            <>
                              $0 - <LtcCostDisplay value={costData.cost} />
                            </>
                          ) : (
                            <LtcCostDisplay value={costData?.cost ?? 0} />
                          );
                        })()}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <SlideOutSheet
                slideOutContent={<CareSettingsSlideOutContent />}
                title="FAQ"
              >
                <div className="text-primary hover:text-primary-hover ml-1 mt-4 text-left text-sm underline md:text-base">
                  What are care settings?
                </div>
              </SlideOutSheet>
            </div>
          </div>
          <CarePhaseCostSummary
            client={client}
            carePhase={carePhase}
          />
        </div>
      </div>
    </OnboardingLayout>
  );
}

export function OnboardingCareSettingsPhaseOne({ clientId }: ClientIdProps) {
  return (
    <OnboardingCareSettings
      carePhase={carePhaseDefs.earlyCare.value}
      slidePage={OnboardingCareSettingsPhaseOne}
    />
  );
}

export function OnboardingCareSettingsPhaseTwo({ clientId }: ClientIdProps) {
  return (
    <OnboardingCareSettings
      carePhase={carePhaseDefs.moderateCare.value}
      slidePage={OnboardingCareSettingsPhaseTwo}
    />
  );
}

export function OnboardingCareSettingsPhaseThree({ clientId }: ClientIdProps) {
  return (
    <OnboardingCareSettings
      carePhase={carePhaseDefs.fullCare.value}
      slidePage={OnboardingCareSettingsPhaseThree}
    />
  );
}

function determineNextSlide(isCareSettingHome: boolean, carePhase: CarePhase) {
  if (isCareSettingHome) {
    switch (carePhaseDefs[carePhase].index) {
      case 0:
        return OnboardingFamilyCaregivingPhaseOne;
      case 1:
        return OnboardingFamilyCaregivingPhaseTwo;
      case 2:
        return OnboardingFamilyCaregivingPhaseThree;
      default:
        return undefined;
    }
  }

  // When not home care, go directly to next care settings phase or summary
  switch (carePhaseDefs[carePhase].index) {
    case 0:
      return OnboardingCareSettingsPhaseTwo;
    case 1:
      return OnboardingCareSettingsPhaseThree;
    case 2:
      return OnboardingCarePhasesSummary;
    default:
      return undefined;
  }
}
